// Softphone global (Fase 7) — ÚNICO origen de verdad del estado de llamada.
//
// Port funcional del PhoneProviderContext del CRM (crm/apps/web/src/features/
// phone/PhoneProviderContext.tsx) adaptado a las convenciones de este SPA
// (sin react-query, inline styles, español). Responsabilidades:
//
//   - Preferencia de proveedor por usuario (GET/PUT /preferences/phone):
//     'cloudtalk' (default; click-to-call server-side, el audio suena en la app
//     de agente de Cloudtalk) o 'telnyx' (WebRTC: el audio vive en el navegador).
//   - Ciclo de vida de la llamada saliente: start → sonando → en llamada →
//     colgada. Estados EN VIVO vía Mercure (topic del lead, evento
//     phone.call.status publicado por crm/apps/jobs); si el hub no está
//     accesible degrada con elegancia a estados por acciones locales.
//   - Integración Telnyx: import() dinámico del engine (solo si el proveedor es
//     telnyx), <audio> remoto montado aquí, auto-answer del leg de bridge.
//   - Presencia: heartbeat (60s) mientras la sesión CRM esté conectada +
//     call-state (idle/ringing/on_call) en cada transición con keepalive 15s.
//   - Transferencia atendida (consult/complete/cancel).
//
// Todos los efectos limpian sus timers/EventSource/SDK en unmount y cuando la
// sesión CRM se desconecta (los intervalos se gatean con `connected === true`).

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CrmHttpError, describeCrmError } from "../api/crm";
import {
  canonicalLeadId,
  dialNumber,
  getPhonePrefs,
  getRealtimeToken,
  hangupCall,
  holdCall,
  leadTopic,
  presenceHeartbeat,
  putPhonePrefs,
  reportCallState,
  resolveByPhone,
  startCall,
  transferCancel,
  transferComplete,
  transferConsult,
} from "../api/crmPhone";
import type { DialMarket } from "../api/crmTypes";
// `import type`: PhoneProvider (el tipo) colisiona con el componente homónimo
// exportado abajo — Babel/CRA rechaza el binding duplicado aunque tsc lo acepte.
import type {
  CallState,
  PhoneCallLiveStatus,
  PhoneCallStatusEvent,
  PhoneProvider,
} from "../api/crmTypes";
import { useCrmSession } from "./CrmSessionContext";
import type { PhoneState, TelnyxPhone } from "./telnyxPhone";

// Timings (espejo del CRM).
const REREGISTER_MS = 20 * 60 * 60 * 1000; // re-login Telnyx antes de que caduque el token (~24h)
const REREGISTER_RETRY_MS = 5 * 60 * 1000; // reintento si había llamada viva
const CALL_STATE_KEEPALIVE_MS = 15_000;
const HEARTBEAT_MS = 60_000;
const ENDED_CLEAR_MS = 6_000; // la llamada colgada se muestra un momento y se limpia

const REMOTE_AUDIO_ID = "telnyx-remote-audio";

export type ActiveCall = {
  /** callId del orquestador (el que aceptan hangup/hold/transfer). */
  callId: string;
  /** Lead de la llamada (canónico Crockford para la URL/notas). null en el
   *  dialer libre cuando el número marcado no pertenece a ningún lead: la
   *  consola se muestra sin ficha y las notas quedan deshabilitadas. */
  leadUlid: string | null;
  leadName: string | null;
  phone: string;
  provider: PhoneProvider;
  /** Estado en vivo (initiated=sonando, answered=en llamada, completed=colgada). */
  live: PhoneCallLiveStatus;
  /** Epoch ms de cuando pasó a answered (para el cronómetro del widget). */
  answeredAt: number | null;
  /** Hold server-side del leg del cliente. */
  onHold: boolean;
  /** Telnyx: el SDK reporta el leg de bridge 'active' pero Mercure aún no ha
   *  confirmado que el CLIENTE contestó. Estado intermedio "conectando…":
   *  live sigue en 'initiated' hasta que Mercure sella 'answered'. Con Mercure
   *  caído, el fallback marca answered directamente (ver transiciones). */
  connecting: boolean;
};

export type TransferState = {
  phase: "idle" | "consulting";
  targetEmail: string | null;
  pending: boolean;
  error: string | null;
};

const IDLE_TRANSFER: TransferState = {
  phase: "idle",
  targetEmail: null,
  pending: false,
  error: null,
};

type PhoneContextValue = {
  provider: PhoneProvider;
  /** false hasta que la preferencia cargó (o falló y aplicó el default). */
  prefsReady: boolean;
  setProvider: (p: PhoneProvider) => void;
  /** Estado del softphone Telnyx (offline si el proveedor es cloudtalk). */
  softphone: PhoneState;
  /** Reintenta el registro del softphone Telnyx tras un fallo. */
  reconnectSoftphone: () => void;
  activeCall: ActiveCall | null;
  /** Inicia la llamada al lead. Lanza CrmHttpError/Error si falla (el caller
   *  pinta el mensaje); si arranca, el estado vive en `activeCall`. */
  startLeadCall: (args: {
    leadUlid: string;
    phone: string;
    leadName: string | null;
  }) => Promise<void>;
  /** Dialer libre: marca cualquier número. Normaliza a E.164 (server, hint de
   *  market default ES), resuelve el lead del número (si lo hay) para mostrarlo
   *  en la consola, y entra en el MISMO ciclo de llamada que startLeadCall.
   *  Lanza CrmHttpError/Error si falla (el caller pinta el mensaje). */
  startDialCall: (args: { number: string; market?: DialMarket }) => Promise<void>;
  /** true si el pre-warm del micrófono fue rechazado (permiso denegado): el
   *  CallButton avisa antes de intentar una llamada Telnyx sin audio. Solo
   *  relevante con provider==='telnyx'. */
  micDenied: boolean;
  /** Reintenta el permiso de micrófono con gesto de usuario (click). true si
   *  quedó concedido. Si el navegador lo tiene BLOQUEADO no habrá prompt: hay
   *  que desbloquearlo en la configuración del sitio. */
  retryMicPermission: () => Promise<boolean>;
  hangup: () => void;
  toggleHold: () => void;
  toggleMute: () => void;
  /** Cierra el widget de una llamada ya colgada (o pierde el rastro cloudtalk). */
  dismissCall: () => void;
  /** null = aún no probado; false = hub Mercure inaccesible (estados solo locales). */
  realtimeOk: boolean | null;
  /** Último error no-fatal de una acción del widget (hold/transfer/hangup). */
  actionError: string | null;
  transfer: TransferState;
  startTransfer: (targetEmail: string) => Promise<void>;
  completeTransfer: () => Promise<void>;
  cancelTransfer: () => Promise<void>;
};

const PhoneContext = createContext<PhoneContextValue | null>(null);

function toActionError(err: unknown): string {
  if (err instanceof CrmHttpError) return describeCrmError(err);
  return err instanceof Error ? err.message : "Error inesperado";
}

export function PhoneProvider({ children }: { children: ReactNode }) {
  const { connected } = useCrmSession();

  const [provider, setProviderState] = useState<PhoneProvider>("cloudtalk");
  const [prefsReady, setPrefsReady] = useState(false);
  const [softphone, setSoftphone] = useState<PhoneState>({ status: "offline" });
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [realtimeOk, setRealtimeOk] = useState<boolean | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [transfer, setTransfer] = useState<TransferState>(IDLE_TRANSFER);

  // Instancia del engine Telnyx (solo cuando provider==='telnyx').
  const telnyxRef = useRef<TelnyxPhone | null>(null);
  // Leg de consulta de la transferencia en curso (para complete/cancel).
  const consultLegRef = useRef<string | null>(null);
  // callId activo, legible desde efectos/closures sin re-suscribirlos.
  const activeCallRef = useRef<ActiveCall | null>(null);
  activeCallRef.current = activeCall;
  const endedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Coordinación start ↔ hangup / transiciones Telnyx ────────────────────
  // Hay un startCall() en vuelo (aún sin callId): el botón Colgar no puede
  // actuar todavía, así que registramos la INTENCIÓN de colgar y la aplicamos
  // en cuanto el start resuelve (fix: colgar durante start en vuelo).
  const startInFlightRef = useRef(false);
  const hangupRequestedRef = useRef(false);
  // Último status del engine Telnyx que llegó ANTES de que existiera activeCall
  // (p.ej. el leg de bridge se activó mientras startCall() seguía en vuelo). Se
  // aplica al hacer setActiveCall para no perder la transición (fix 3a).
  const pendingTelnyxStatusRef = useRef<PhoneState["status"] | null>(null);
  // Confirmación Mercure de 'answered' para la llamada Telnyx en curso: mientras
  // no llegue, el SDK 'active' solo marca 'conectando' (fix 3b).
  const mercureAnsweredRef = useRef(false);
  // Timer de gracia para un 'offline' TRANSITORIO del SDK: solo completa la
  // llamada si el offline persiste >5s (fix 3c).
  const offlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Preferencia de proveedor (por usuario, scope 'phone') ────────────────
  useEffect(() => {
    if (connected !== true) return;
    let cancelled = false;
    getPhonePrefs()
      .then((env) => {
        if (cancelled) return;
        setProviderState(env.value?.provider ?? "cloudtalk");
        setPrefsReady(true);
      })
      .catch(() => {
        // Sin preferencia legible (p.ej. red): default cloudtalk, no bloquea.
        if (!cancelled) setPrefsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [connected]);

  const setProvider = useCallback((p: PhoneProvider) => {
    // Optimista: el selector responde al instante; si el PUT falla se revierte.
    setProviderState((prev) => {
      if (prev === p) return prev;
      putPhonePrefs({ provider: p }).catch((err) => {
        setProviderState(prev);
        setActionError(toActionError(err));
      });
      return p;
    });
  }, []);

  // ── Ciclo de vida del softphone Telnyx (SDK dinámico) ────────────────────
  useEffect(() => {
    if (connected !== true || provider !== "telnyx") return;
    let disposed = false;
    let phone: TelnyxPhone | null = null;
    let reregisterInterval: ReturnType<typeof setInterval> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    // import() dinámico: @telnyx/webrtc solo se descarga si el proveedor es telnyx.
    void import("./telnyxPhone").then(({ TelnyxPhone: Engine }) => {
      if (disposed) return;
      phone = new Engine();
      phone.onState = setSoftphone;
      telnyxRef.current = phone;
      void phone.connect(REMOTE_AUDIO_ID);

      const tryReregister = () => {
        // Alineado con el CRM (PhoneProviderContext:191): si el efecto se limpió
        // (cambio de proveedor / desconexión) NO reconectamos — connect() haría
        // teardown de un cliente que ya estamos desmontando.
        if (disposed || !phone) return;
        if (phone.hasLiveCall()) {
          // No tirar una conversación en curso: reintenta en 5 min.
          retryTimer = setTimeout(tryReregister, REREGISTER_RETRY_MS);
          return;
        }
        void phone.connect(REMOTE_AUDIO_ID);
      };
      reregisterInterval = setInterval(tryReregister, REREGISTER_MS);
    });

    return () => {
      disposed = true;
      if (reregisterInterval) clearInterval(reregisterInterval);
      if (retryTimer) clearTimeout(retryTimer);
      telnyxRef.current = null;
      void phone?.disconnect();
      setSoftphone({ status: "offline" });
    };
  }, [connected, provider]);

  const reconnectSoftphone = useCallback(() => {
    void telnyxRef.current?.connect(REMOTE_AUDIO_ID);
  }, []);

  // ── Transiciones locales Telnyx → estado de llamada ──────────────────────
  // El SDK es la señal más inmediata cuando el audio vive en el navegador, pero
  // NO es autoritativo sobre si el CLIENTE contestó: el leg de bridge del agente
  // pasa a 'active' por su auto-answer, no porque el cliente descuelgue. Por eso:
  //   - 'active' → estado intermedio "conectando"; Mercure confirma 'answered'.
  //     Fallback (Mercure caído, realtimeOk===false): answered directo.
  //   - 'idle' (hangup limpio del SDK) → completed.
  //   - 'offline' (caída de socket, potencialmente transitoria) → NO completar
  //     de inmediato: exige que persista >5s (fix 3c); si vuelve antes, se
  //     cancela. Un hangup local o Mercure 'completed' completan igualmente.
  const applyTelnyxStatus = useCallback(
    (status: PhoneState["status"]) => {
      const call = activeCallRef.current;
      // Fix 3a: transición del engine llegada mientras el start sigue en vuelo
      // (aún no hay activeCall). La guardamos para aplicarla al setActiveCall.
      if (!call) {
        if (startInFlightRef.current) pendingTelnyxStatusRef.current = status;
        return;
      }
      if (call.provider !== "telnyx" || call.live === "completed") return;

      // Cualquier status distinto de offline cancela el timer de gracia.
      if (status !== "offline" && offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }

      if (status === "active" || status === "held") {
        if (call.live === "initiated") {
          const mercureDown = realtimeOk === false;
          if (mercureAnsweredRef.current || mercureDown) {
            setActiveCall((c) =>
              c && c.live === "initiated"
                ? { ...c, live: "answered", answeredAt: Date.now(), connecting: false }
                : c,
            );
          } else {
            // Bridge activo pero el cliente aún no confirmado por Mercure.
            setActiveCall((c) =>
              c && c.live === "initiated" && !c.connecting
                ? { ...c, connecting: true }
                : c,
            );
          }
        }
        return;
      }

      if (status === "idle") {
        setActiveCall((c) =>
          c && c.live !== "completed" ? { ...c, live: "completed" } : c,
        );
        return;
      }

      if (status === "offline") {
        // Puede ser un blip de socket con reconexión automática del engine: no
        // tiramos la llamada hasta confirmar que persiste >5s.
        if (offlineTimerRef.current) return;
        offlineTimerRef.current = setTimeout(() => {
          offlineTimerRef.current = null;
          setActiveCall((c) =>
            c && c.provider === "telnyx" && c.live !== "completed"
              ? { ...c, live: "completed" }
              : c,
          );
        }, 5_000);
      }
    },
    [realtimeOk],
  );

  useEffect(() => {
    applyTelnyxStatus(softphone.status);
  }, [softphone.status, applyTelnyxStatus]);

  // ── Llamada colgada: se muestra un momento y se limpia sola ──────────────
  useEffect(() => {
    if (activeCall?.live !== "completed") return;
    endedTimerRef.current = setTimeout(() => {
      setActiveCall(null);
      setTransfer(IDLE_TRANSFER);
      consultLegRef.current = null;
      mercureAnsweredRef.current = false;
      setActionError(null);
    }, ENDED_CLEAR_MS);
    return () => {
      if (endedTimerRef.current) clearTimeout(endedTimerRef.current);
      endedTimerRef.current = null;
    };
  }, [activeCall?.live]);

  // ── Estados en vivo vía Mercure (topic del lead) ─────────────────────────
  // Suscripción SOLO mientras hay llamada activa (Operations no necesita el
  // stream en reposo). Descubrimiento del hub en runtime: GET /realtime/token
  // devuelve hubUrl y refresca la cookie httpOnly mercureAuthorization (cross-
  // subdominio, Fase 0). Si el hub no está accesible: realtimeOk=false y los
  // estados avanzan solo por acciones locales (degradación elegante).
  const subscribedLead = activeCall && activeCall.live !== "completed"
    ? activeCall.leadUlid
    : null;
  useEffect(() => {
    if (connected !== true || !subscribedLead) return;
    let cancelled = false;
    let es: EventSource | null = null;
    // Timer de gracia: si onopen no llega en OPEN_GRACE_MS, degradamos (fix 4a).
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    // Reconexión propia con backoff: EventSource reconecta solo, pero no refresca
    // la cookie httpOnly ni se rinde; ante onerror PERSISTENTE (>=3 sin onopen
    // intermedio) cerramos, re-pedimos token (refresca cookie) y reconectamos.
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let errorStreak = 0;
    let reconnectAttempts = 0;
    const OPEN_GRACE_MS = 8_000;
    const MAX_ERROR_STREAK = 3;

    const applyAnswered = () => {
      mercureAnsweredRef.current = true;
      setActiveCall((c) =>
        c && c.live === "initiated"
          ? { ...c, live: "answered", answeredAt: Date.now(), connecting: false }
          : c,
      );
    };

    const clearTimers = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    const open = () => {
      getRealtimeToken()
        .then((rt) => {
          if (cancelled) return;
          if (!rt.enabled || !rt.hubUrl) {
            setRealtimeOk(false);
            return;
          }
          const url = new URL(rt.hubUrl);
          url.searchParams.append("topic", leadTopic(subscribedLead));
          es = new EventSource(url.toString(), { withCredentials: true });

          // Gracia: sin onopen a tiempo → avisamos degradación (no cerramos: el
          // EventSource puede aún abrir; onopen limpiará realtimeOk a true).
          graceTimer = setTimeout(() => {
            graceTimer = null;
            if (!cancelled) setRealtimeOk(false);
          }, OPEN_GRACE_MS);

          es.onopen = () => {
            errorStreak = 0;
            reconnectAttempts = 0;
            clearTimers();
            setRealtimeOk(true);
          };

          es.onmessage = (e: MessageEvent) => {
            let parsed: unknown;
            try {
              parsed = JSON.parse(e.data as string);
            } catch {
              return; // evento no-JSON: se ignora
            }
            const ev = parsed as Partial<PhoneCallStatusEvent>;
            if (ev.type !== "phone.call.status" || !ev.status) return;
            if (ev.callId && ev.callId !== activeCallRef.current?.callId) return;
            if (ev.status === "answered") {
              applyAnswered();
            } else if (ev.status === "completed") {
              setActiveCall((c) =>
                c && c.live !== "completed" ? { ...c, live: "completed" } : c,
              );
            }
          };

          es.onerror = () => {
            errorStreak += 1;
            if (errorStreak < MAX_ERROR_STREAK) return; // deja reconectar nativo
            // Error persistente: cerramos, refrescamos cookie y reconectamos con
            // backoff (fix 4b). No perdemos el aviso de degradación entretanto.
            setRealtimeOk(false);
            es?.close();
            es = null;
            errorStreak = 0;
            clearTimers();
            if (cancelled) return;
            const delay = Math.min(30_000, 2_000 * 2 ** reconnectAttempts);
            reconnectAttempts += 1;
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              if (!cancelled) open();
            }, delay);
          };
        })
        .catch(() => {
          if (!cancelled) setRealtimeOk(false);
        });
    };

    open();

    return () => {
      cancelled = true;
      clearTimers();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [connected, subscribedLead]);

  // ── Presencia: heartbeat mientras la sesión CRM esté conectada ───────────
  useEffect(() => {
    if (connected !== true) return;
    const ping = () => presenceHeartbeat().catch(() => {});
    ping();
    const interval = setInterval(ping, HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [connected]);

  // ── Presencia: call-state en cada transición + keepalive en llamada ──────
  // Derivado de PRIMITIVOS (no del objeto activeCall entero): así el keepalive de
  // presencia no se recrea por mutaciones de onHold/answeredAt (fix limpieza).
  const activeLive = activeCall?.live ?? null;
  const callState: CallState = useMemo(() => {
    if (provider === "telnyx") {
      if (softphone.status === "active" || softphone.status === "held") {
        return "on_call";
      }
      return softphone.status === "ringing" ? "ringing" : "idle";
    }
    if (!activeLive || activeLive === "completed") return "idle";
    return activeLive === "answered" ? "on_call" : "ringing";
  }, [provider, softphone.status, activeLive]);

  useEffect(() => {
    if (connected !== true) return;
    reportCallState(callState).catch(() => {});
    if (callState !== "on_call") return;
    const interval = setInterval(() => {
      reportCallState("on_call").catch(() => {});
    }, CALL_STATE_KEEPALIVE_MS);
    return () => clearInterval(interval);
  }, [connected, callState]);

  // ── Acciones ─────────────────────────────────────────────────────────────

  // Núcleo compartido del arranque de llamada: máquina de coordinación start ↔
  // hangup ↔ transiciones Telnyx (bridge/pending/answered-early). Lo usan tanto
  // la llamada a lead (startLeadCall) como el dialer libre (startDialCall): NO
  // se duplica la máquina de estados. `starter` dispara la llamada en el CRM y
  // devuelve el callId + la identidad a pintar en la consola (para el dialer la
  // identidad la conocemos DESPUÉS de la respuesta/resolución del número).
  const beginCall = useCallback(
    async (
      starter: () => Promise<{
        callId: string;
        identity: { leadUlid: string | null; phone: string; leadName: string | null };
      }>,
    ) => {
      if (activeCallRef.current && activeCallRef.current.live !== "completed") {
        throw new Error("Ya hay una llamada en curso");
      }
      if (provider === "telnyx") {
        if (softphone.status !== "idle") {
          throw new Error(
            "El softphone Telnyx no está registrado todavía (o hay otra llamada). " +
              "Espera al estado 'listo' o cambia a Cloudtalk.",
          );
        }
        // El próximo leg entrante del SDK es NUESTRO bridge → auto-contestar.
        telnyxRef.current?.armBridgeLeg();
      }
      setActionError(null);
      // Estado inicial de coordinación del start en vuelo.
      startInFlightRef.current = true;
      hangupRequestedRef.current = false;
      pendingTelnyxStatusRef.current = null;
      mercureAnsweredRef.current = false;
      let res: { callId: string; identity: { leadUlid: string | null; phone: string; leadName: string | null } };
      try {
        res = await starter();
      } catch (err) {
        // Fix 2: el start falló → no habrá bridge, desarma el auto-answer para
        // que un inbound REAL posterior suene en vez de auto-contestarse.
        startInFlightRef.current = false;
        hangupRequestedRef.current = false;
        if (provider === "telnyx") telnyxRef.current?.disarmBridgeLeg();
        throw err;
      }
      startInFlightRef.current = false;

      // Fix 1: si pidieron colgar mientras el start estaba en vuelo, ejecuta el
      // hangup ya (no montamos el widget de una llamada que el asesor abortó).
      if (hangupRequestedRef.current) {
        hangupRequestedRef.current = false;
        if (provider === "telnyx") telnyxRef.current?.disarmBridgeLeg();
        telnyxRef.current?.hangup();
        hangupCall(res.callId).catch(() => {});
        return;
      }

      // Fix 3a: aplica cualquier transición del engine llegada antes de existir
      // activeCall (p.ej. el bridge se activó durante el start en vuelo).
      const pending = pendingTelnyxStatusRef.current;
      pendingTelnyxStatusRef.current = null;
      const answeredEarly =
        provider === "telnyx" &&
        (pending === "active" || pending === "held") &&
        (mercureAnsweredRef.current || realtimeOk === false);
      setActiveCall({
        callId: res.callId,
        leadUlid: res.identity.leadUlid,
        leadName: res.identity.leadName,
        phone: res.identity.phone,
        provider,
        live: answeredEarly ? "answered" : "initiated",
        answeredAt: answeredEarly ? Date.now() : null,
        onHold: false,
        connecting:
          provider === "telnyx" &&
          !answeredEarly &&
          (pending === "active" || pending === "held"),
      });
    },
    [provider, softphone.status, realtimeOk],
  );

  const startLeadCall = useCallback(
    async (args: { leadUlid: string; phone: string; leadName: string | null }) =>
      beginCall(async () => {
        const res = await startCall(args.leadUlid, { provider });
        return {
          callId: res.callId,
          identity: {
            leadUlid: args.leadUlid,
            phone: args.phone,
            leadName: args.leadName,
          },
        };
      }),
    [beginCall, provider],
  );

  const startDialCall = useCallback(
    async (args: { number: string; market?: DialMarket }) =>
      beginCall(async () => {
        const market = args.market ?? "ES";
        const res = await dialNumber({
          toNumber: args.number,
          market,
          provider,
        });
        // Si el número pertenece a un lead, resolvemos su nombre para pintarlo en
        // la consola (best-effort: un fallo no aborta la llamada ya en curso).
        // El leadId hex32 del dial se canonicaliza para la URL/notas de la consola.
        let leadUlid: string | null = res.leadId ? canonicalLeadId(res.leadId) : null;
        let leadName: string | null = null;
        let phone = args.number.trim();
        try {
          const resolved = await resolveByPhone(args.number);
          if (resolved.phone) phone = resolved.phone;
          if (resolved.leadName) leadName = resolved.leadName;
          if (!leadUlid && resolved.leadId) leadUlid = canonicalLeadId(resolved.leadId);
        } catch {
          // Resolución fallida: la consola muestra la llamada sin ficha/nombre.
        }
        return { callId: res.callId, identity: { leadUlid, phone, leadName } };
      }),
    [beginCall, provider],
  );

  const hangup = useCallback(() => {
    const call = activeCallRef.current;
    // Fix 1: aún no hay callId (start en vuelo) → registra la intención; se
    // aplicará al resolver el start. Así el asesor puede abortar sin esperar.
    if (!call && startInFlightRef.current) {
      hangupRequestedRef.current = true;
      return;
    }
    // Si hay una consulta de transferencia viva, cancelarla primero (best-effort)
    // para no dejar al cliente parkeado con la asesora destino sonando.
    if (call && consultLegRef.current) {
      transferCancel(call.callId, consultLegRef.current).catch(() => {});
      consultLegRef.current = null;
      setTransfer(IDLE_TRANSFER);
    }
    // Telnyx: corta el leg del navegador; el server-hangup corta el del cliente.
    telnyxRef.current?.disarmBridgeLeg();
    telnyxRef.current?.hangup();
    if (call) {
      hangupCall(call.callId)
        .then((r) => {
          if (!r.ok) {
            setActionError(
              "El orquestador no pudo colgar el leg del cliente (se cerrará solo al terminar).",
            );
          }
        })
        .catch((err) => setActionError(toActionError(err)));
      setActiveCall((c) => (c ? { ...c, live: "completed" } : c));
    }
  }, []);

  const toggleHold = useCallback(() => {
    const call = activeCallRef.current;
    if (!call || call.live !== "answered") return;
    const next = !call.onHold;
    // Telnyx: hold local del SDK (autoritativo para el leg del agente)…
    if (call.provider === "telnyx") telnyxRef.current?.toggleHold();
    // …y en ambos proveedores, hold server-side del leg del CLIENTE (con música).
    holdCall(call.callId, next)
      .then((r) => {
        if (!r.ok) setActionError("El orquestador no pudo cambiar el hold.");
      })
      .catch((err) => setActionError(toActionError(err)));
    setActiveCall((c) => (c ? { ...c, onHold: next } : c));
  }, []);

  const toggleMute = useCallback(() => {
    telnyxRef.current?.toggleMute();
  }, []);

  const dismissCall = useCallback(() => {
    if (endedTimerRef.current) clearTimeout(endedTimerRef.current);
    endedTimerRef.current = null;
    if (offlineTimerRef.current) clearTimeout(offlineTimerRef.current);
    offlineTimerRef.current = null;
    setActiveCall(null);
    setTransfer(IDLE_TRANSFER);
    consultLegRef.current = null;
    mercureAnsweredRef.current = false;
    setActionError(null);
  }, []);

  // ── Transferencia atendida ───────────────────────────────────────────────

  const startTransfer = useCallback(async (targetEmail: string) => {
    const call = activeCallRef.current;
    if (!call || call.live !== "answered") return;
    setTransfer({ phase: "idle", targetEmail, pending: true, error: null });
    try {
      const res = await transferConsult(call.callId, targetEmail);
      if (activeCallRef.current?.callId !== call.callId) {
        // La llamada activa cambió durante el consult: cancela el leg de la
        // destino (best-effort) y resetea el estado de transfer a idle (fix 5).
        transferCancel(call.callId, res.consultCallControlId).catch(() => {});
        setTransfer(IDLE_TRANSFER);
        return;
      }
      consultLegRef.current = res.consultCallControlId;
      setTransfer({
        phase: "consulting",
        targetEmail: res.targetAgentEmail,
        pending: false,
        error: null,
      });
    } catch (err) {
      setTransfer({ ...IDLE_TRANSFER, error: toActionError(err) });
    }
  }, []);

  const completeTransfer = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call) return;
    setTransfer((t) => ({ ...t, pending: true, error: null }));
    try {
      await transferComplete(call.callId, {
        // Opcional: si el front lo perdió, el server lo recupera de su sellado.
        // leadId se OMITE: el consultCallControlId + el sellado del server basta
        // para identificar la transferencia (fix 5).
        consultCallControlId: consultLegRef.current ?? undefined,
        targetAgentEmail: transfer.targetEmail ?? undefined,
      });
      consultLegRef.current = null;
      setTransfer(IDLE_TRANSFER);
      // El cliente queda con la destino: nuestra llamada termina.
      telnyxRef.current?.hangup();
      setActiveCall((c) => (c ? { ...c, live: "completed" } : c));
    } catch (err) {
      setTransfer((t) => ({ ...t, pending: false, error: toActionError(err) }));
    }
  }, [transfer.targetEmail]);

  const cancelTransfer = useCallback(async () => {
    const call = activeCallRef.current;
    const leg = consultLegRef.current;
    consultLegRef.current = null;
    setTransfer(IDLE_TRANSFER);
    if (!call) return;
    try {
      await transferCancel(call.callId, leg ?? undefined);
    } catch (err) {
      // No-fatal: el iniciador sigue con el cliente; se avisa igualmente.
      setActionError(toActionError(err));
    }
  }, []);

  const value = useMemo<PhoneContextValue>(
    () => ({
      provider,
      prefsReady,
      setProvider,
      softphone,
      reconnectSoftphone,
      micDenied: softphone.micDenied === true,
      retryMicPermission: async () =>
        (await telnyxRef.current?.retryMic()) === true,
      activeCall,
      startLeadCall,
      startDialCall,
      hangup,
      toggleHold,
      toggleMute,
      dismissCall,
      realtimeOk,
      actionError,
      transfer,
      startTransfer,
      completeTransfer,
      cancelTransfer,
    }),
    [
      provider,
      prefsReady,
      setProvider,
      softphone,
      reconnectSoftphone,
      activeCall,
      startLeadCall,
      startDialCall,
      hangup,
      toggleHold,
      toggleMute,
      dismissCall,
      realtimeOk,
      actionError,
      transfer,
      startTransfer,
      completeTransfer,
      cancelTransfer,
    ],
  );

  return (
    <PhoneContext.Provider value={value}>
      {children}
      {/* El <audio> remoto del softphone Telnyx DEBE existir en el DOM antes de
          connect(): el SDK enruta aquí el audio del bridge. Inaudible/invisible
          con cloudtalk (nunca recibe stream). */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio id={REMOTE_AUDIO_ID} autoPlay />
    </PhoneContext.Provider>
  );
}

export function usePhone(): PhoneContextValue {
  const ctx = useContext(PhoneContext);
  if (!ctx) throw new Error("usePhone debe usarse dentro de <PhoneProvider>");
  return ctx;
}
